import React, { memo, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { exportYieldData } from "api/business-user-management";
import {
  IExportYieldPayload,
  TYieldDisplayByType,
} from "api/business-user-management/types";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeWords,
  formatDateToYYYYMMDD,
  handleDownloadBufferData,
  haveQueryParamsChanged,
} from "utils";
import {
  NO_EXPORT_DATA_FOUND,
  NO_NEW_FILTER_MESSAGE,
  ZERO,
} from "utils/constants";

import {
  customErrorToast,
  customInfoToast,
  customSuccessToast,
  customWarningToast,
} from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { Export } from "ui/export";
import { IExportDateRangeValues, TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";

import { DEFAULT_YIELD_PAGINATION_PARAMS } from "./const";
import {
  IBusinessYieldTableHeaderCTAsProps,
  IGetBusinessYieldPayload,
} from "./types";

const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];
export const BusinessYieldTableHeaderCTAs = memo(
  (props: IBusinessYieldTableHeaderCTAsProps) => {
    const {
      handleGetBusinessYield,
      isLoadingYield,
      isRefreshing,
      setIsRefreshing,
      filterType,
    } = props;
    const filterRef = useRef<IFilterRef | null>(null);
    const { businessId } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const displayByType = searchParams.get("displayByType");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const updateQueryParams = useUpdateQueryParams();
    const { isFilterError, setIsFilterError, filterRetryCountParams } =
      useFilterQueryParams({
        apiFilterRoute: "/control/business/compliance/yield",
      });
    const [isExporting, setIsExporting] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [filterValues, setFilterValues] = useState<TFilterValues>({
      period: displayByType || filterType || "",
      dateRange: {
        startDate,
        endDate,
      },
    });

    const isFilterDisabled = !(
      filterValues?.period ||
      (filterValues?.dateRange as IDateRangeValues)?.startDate
    );

    const handleResetFilter = () => {
      setFilterValues({
        period: "",
        dateRange: {
          startDate: null,
          endDate: null,
        },
      });
    };

    const handleFilterValueChange = (key: string, value: TFilterValue) => {
      setFilterValues((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const handleApplyFilter = async () => {
      const filterPayload: IGetBusinessYieldPayload = {
        startDate: "",
        endDate: "",
        displayByType: "days",
        businessId: businessId || "",
        isRefetching: false,
        page: Number(DEFAULT_YIELD_PAGINATION_PARAMS.page),
        limit: Number(DEFAULT_YIELD_PAGINATION_PARAMS.limit),
      };

      const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
      const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
      const displayByType = filterValues.period as TYieldDisplayByType;
      const newQueryParams: Record<string, string> = {};

      if (startDate) {
        filterPayload["startDate"] = startDate;
        newQueryParams["startDate"] = startDate;
      }

      if (endDate) {
        filterPayload["endDate"] = endDate;
        newQueryParams["endDate"] = endDate;
      }

      if (displayByType) {
        filterPayload["displayByType"] = displayByType;
        newQueryParams["displayByType"] = displayByType;
      }

      newQueryParams["page"] = DEFAULT_YIELD_PAGINATION_PARAMS.page.toString();
      newQueryParams["limit"] =
        DEFAULT_YIELD_PAGINATION_PARAMS.limit.toString();

      setIsFilterError(false);
      updateQueryParams({
        ...newQueryParams,
        ...filterRetryCountParams,
      });
      if (
        haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: newQueryParams,
        }) ||
        isFilterError
      ) {
        setIsFiltering(true);
      } else {
        customWarningToast({
          message: NO_NEW_FILTER_MESSAGE,
        });
      }
    };

    const handleRefreshTable = async () => {
      setIsRefreshing(true);
      if (!businessId) {
        console.error("No business id");
        return;
      }
      if (
        !haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: {},
        })
      ) {
        await handleGetBusinessYield({
          businessId,
          displayByType: "days",
          page: DEFAULT_YIELD_PAGINATION_PARAMS.page,
          limit: DEFAULT_YIELD_PAGINATION_PARAMS.limit,
          isRefetching: true,
        });
        setIsRefreshing(false);
      } else {
        updateQueryParams({});
      }
    };

    const dropDownRef = useRef<IDropDownRef | null>(null);
    const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
      startDate: null,
      endDate: null,
    });
    const [exportType, setExportType] = useState<TExportType | "">("");

    const handleExportTypeChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setExportType(value as TExportType);
    };

    const handleDateRangeChange = (name: string, value: Date | null) => {
      if (!value) return;
      setDateRange({
        ...dateRange,
        [name]: value,
      });
    };

    const handleResetExport = () => {
      setDateRange({
        startDate: null,
        endDate: null,
      });
      setExportType("");
    };

    const handleExport = async () => {
      const { startDate, endDate } = dateRange;
      if (!startDate || !endDate || !businessId || !exportType) return;
      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);
      setIsExporting(true);
      const exportPayload: IExportYieldPayload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        format: exportType,
        businessId,
      };
      try {
        const { data, error, message } = await exportYieldData(exportPayload);
        if (error) {
          customErrorToast({ message });
        } else if (data?.data) {
          const exportData = data?.data;
          if (exportData.length > ZERO) {
            handleDownloadBufferData({
              data: exportData,
              name: `Yield-${formattedStartDate}-${formattedEndDate}`,
              type: exportType,
            });
            customSuccessToast({ message: capitalizeWords(message) });
            handleResetExport();
            if (dropDownRef.current) {
              dropDownRef.current.handleToggleDropdown();
            }
          } else {
            customInfoToast({
              message: NO_EXPORT_DATA_FOUND,
            });
          }
        } else {
          customErrorToast({
            message: "No data returned",
          });
        }
      } catch (error) {
        console.error("Error: ", error);
      }
      setIsExporting(false);
    };

    const filterOptions: TFilterOption[] = [
      {
        type: "radio",
        title: "Show By",
        key: "period",
        options: [
          {
            label: "Month",
            value: "month",
          },
          {
            label: "Days",
            value: "days",
          },
        ],
      },
      {
        type: "date-range",
        title: "Custom Date Range",
        key: "dateRange",
      },
    ];

    useEffect(() => {
      if (!isLoadingYield && filterRef?.current?.isFilterOpen) {
        setIsFiltering(false);
        filterRef?.current?.handleToggleFilter();
      }
    }, [isLoadingYield, filterRef]);

    useEffect(() => {
      if (!isLoadingYield && isRefreshing) {
        handleResetFilter();
        setIsRefreshing(false);
      }
    }, [isLoadingYield]);

    return (
      <Box className="flex px-3 items-center justify-between h-[85px] gap-4 w-[331px]">
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    );
  }
);
